import React, {useCallback} from 'react'
import {FileTable} from '@/app/modules/widgets/components/FileTable'
import {useSidePanelResize, type SidePanelResizeOptions} from './useSidePanelResize'
import {PANEL_MIN_WIDTH} from './panelWidth'
import './files-panel.scss'

export interface FilesPanelProps {
  width: number
  onWidthChange: (width: number) => void
  onClose: () => void
  /** Width of the AI chat panel (0 when closed), so the resize drag respects the joint budget. */
  otherPanelWidth: number
  /** Drives the browser to a folder when opened from a deep link. */
  initialPath?: string
  /** Bumped per deep-link request so the same folder can be re-requested. */
  pathRequestNonce?: number
  /**
   * The panel keeps FileTable mounted while collapsed (so selection/scroll survive), and hides the
   * wrapper with `display: none` instead. This flag lets FileTable disable its window-level
   * shortcuts while it is not visible.
   */
  isOpen: boolean
  /** When the centre column is hidden, the per-panel handle is unused (shared divider owns the split). */
  centerCollapsed?: boolean
  onCollapseCenter?: SidePanelResizeOptions['onCollapseCenter']
  onPairResize?: SidePanelResizeOptions['onPairResize']
  /**
   * False for a user without a subscription who has folders shared with them: the browser then
   * shows the Shared root only. Defaults to true.
   */
  ownFiles?: boolean
}

/**
 * Files browser docked to the left of the main content — the mirror of the AI chat panel on the
 * right. Lives outside <Outlet/> in MasterLayout, so route changes (notably the navigate('/results')
 * that fires when a file is opened) leave it untouched.
 */
export const FilesPanel: React.FC<FilesPanelProps> = ({
  width,
  onWidthChange,
  onClose,
  otherPanelWidth,
  initialPath,
  pathRequestNonce,
  isOpen,
  centerCollapsed = false,
  onCollapseCenter,
  onPairResize,
  ownFiles = true,
}) => {
  const getOtherPanelWidth = useCallback(() => otherPanelWidth, [otherPanelWidth])
  const getCenterCollapsed = useCallback(() => centerCollapsed, [centerCollapsed])
  const handleResizePointerDown = useSidePanelResize('left', onWidthChange, getOtherPanelWidth, {
    getCenterCollapsed,
    onCollapseCenter,
    onPairResize,
  })

  return (
    <div
      className='ob-files-panel'
      data-testid='omics-files-panel'
      style={{
        width: `${width}px`,
        minWidth: `${PANEL_MIN_WIDTH}px`,
        maxWidth: centerCollapsed ? undefined : '80vw',
        minHeight: 0,
        // Kept mounted while collapsed so selection, scroll and clipboard survive a close/reopen.
        display: isOpen ? 'flex' : 'none',
        flexDirection: 'column',
        borderRight: centerCollapsed
          ? 'none'
          : '1px solid var(--bs-border-color, #dee2e6)',
        background: 'var(--bs-body-bg, #fff)',
        position: 'relative',
      }}
    >
      <div className='ob-files-panel__body'>
        <FileTable
          title='Files'
          hideTitle
          variant='panel'
          onClose={onClose}
          initialPath={initialPath}
          pathRequestNonce={pathRequestNonce}
          enabled={isOpen}
          ownFiles={ownFiles}
        />
      </div>

      {!centerCollapsed && (
        <div
          className='ob-files-panel__resize-handle'
          data-testid='omics-files-panel-resize-handle'
          onMouseDown={handleResizePointerDown}
          title='Drag to resize'
        />
      )}
    </div>
  )
}
